import { cva, type VariantProps } from 'class-variance-authority';
import { ReactNode } from 'react';
import { Switch, SwitchProps } from 'react-aria-components';
import { cn } from './utils';

const agSwitchTrackVariants = cva(
  'relative inline-flex items-center rounded-full transition-colors duration-200 cursor-pointer',
  {
    variants: {
      size: { sm: 'w-[32px] h-[18px]', md: 'w-[40px] h-[22px]' },
      isSelected: { true: 'bg-brand-6', false: 'bg-neutral-6' },
      isDisabled: { true: 'opacity-50 cursor-not-allowed', false: '' },
    },
    defaultVariants: { size: 'sm', isSelected: false, isDisabled: false },
  }
);

const agSwitchThumbVariants = cva('absolute bg-white rounded-full shadow-sm transition-transform duration-200', {
  variants: {
    size: { sm: 'w-[14px] h-[14px] top-[2px]', md: 'w-[18px] h-[18px] top-[2px]' },
    isSelected: { true: '', false: 'left-[2px]' },
  },
  compoundVariants: [
    { size: 'sm', isSelected: true, className: 'translate-x-[16px]' },
    { size: 'sm', isSelected: false, className: 'translate-x-[2px]' },
    { size: 'md', isSelected: true, className: 'translate-x-[20px]' },
    { size: 'md', isSelected: false, className: 'translate-x-[2px]' },
  ],
  defaultVariants: { size: 'sm', isSelected: false },
});

const agLabelVariants = cva('text-neutral-text', {
  variants: { size: { sm: 'text-13', md: 'text-14' } },
  defaultVariants: { size: 'sm' },
});

type AgSwitchVariantProps = VariantProps<typeof agSwitchTrackVariants>;

export interface AgSwitchProps extends Omit<SwitchProps, 'children'> {
  /** Label text displayed next to the switch */
  label?: ReactNode;
  /** Size variant */
  size?: AgSwitchVariantProps['size'];
  /** Additional class for the container */
  className?: string;
}

export function AgSwitch({ label, size = 'sm', isDisabled, className, ...props }: AgSwitchProps) {
  return (
    <Switch
      isDisabled={isDisabled}
      className={cn('group flex items-center gap-2 cursor-pointer', isDisabled && 'cursor-not-allowed', className)}
      {...props}
    >
      {({ isSelected }) => (
        <>
          <div className={cn(agSwitchTrackVariants({ size, isSelected, isDisabled }))}>
            <div className={cn(agSwitchThumbVariants({ size, isSelected }))} />
          </div>
          {label && <span className={cn(agLabelVariants({ size }))}>{label}</span>}
        </>
      )}
    </Switch>
  );
}

AgSwitch.displayName = 'AgSwitch';
